import { computed, inject, nextTick, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { useTimeoutFn } from '@vueuse/core'

import {
  TaskActionEntryUpdate,
  TaskApiParams,
  TaskApiResponseEntry,
  TaskApiResponseList,
  TaskList,
} from './utils/types'
import { PROCESSING_STATUSES, TaskStatus, TaskType, WSInstance, WSResponse } from '@types'

import { TASK_TIMEOUT } from './utils/const'
import { isFinished, pollTask } from './utils/helpers'

import {
  prepareResponseError,
  removeStoreListItem,
  updateStoreListItem,
} from '../utils/helpers'

import api from '@/store/api'
import { useUserStore } from '@/store/user'

export const useTasksStore = defineStore('tasks', () => {
  // INIT
  const userStore = useUserStore()

  const ws = inject<WSInstance>('ws')

  // STORE
  const list = ref<TaskList>([])
  let observationController = new AbortController()

  // GETTERS
  const getList = computed<TaskList>(() => {
    return list.value
  })

  const getProcessing = computed(() => {
    return getList.value.filter(task =>
      PROCESSING_STATUSES.includes(task.status),
    )
  })

  const getAccountsMapped = computed(() => {
    return getList.value.filter(task =>
      task.status === TaskStatus.ACCOUNTS_MAPPED,
    )
  })

  const getFinished = computed(() => {
    return getList.value.filter(
      task => !PROCESSING_STATUSES.includes(task.status),
    )
  })

  // SETTERS
  const setList = (value: TaskList) => {
    stop()
    list.value = value
    nextTick(() => {
      if (!getProcessing.value.length) return
      start()
    })
  }

  const commit = (tasks: TaskList) => {
    list.value = [...list.value, ...tasks]
  }

  // ACTIONS
  const fetch = async (
    params: TaskApiParams = {
      excluded_statuses: [TaskStatus.SUCCEEDED],
      excluded_types: [TaskType.EOD_SEARCH, TaskType.ANALYTICS_COMPUTE],
    },
  ) => {
    try {
      const preparedParams =
        params &&
        Object.fromEntries(
          Object.keys(params).map(key => [
            key,
            JSON.stringify(params[key as keyof typeof params]),
          ]),
        )
      const response = await api.get<TaskApiResponseList>('tasks', {
        params: preparedParams,
      })
      setList(response.data.data)
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const { start, stop } = useTimeoutFn(fetch, TASK_TIMEOUT)
  stop()

  const fetchSingle = async <T>(taskId: string, signal: AbortSignal) => {
    try {
      const response = await api.get<TaskApiResponseEntry<T>>(`tasks/${taskId}`, { signal })
      signal.throwIfAborted()
      const task = response.data
      const stored = list.value.find(entry => entry.id === task.id)
      // A websocket push can finish the task while this request is in flight.
      // The response is then older than the store and must not undo the push.
      if (isFinished(stored) && !isFinished(task)) return stored
      if (stored) {
        updateStoreListItem(list.value, task)
      } else {
        commit([task])
      }
      return task
    } catch (e) {
      signal.throwIfAborted()
      throw Error(prepareResponseError(e))
    }
  }

  const observe = <T>(taskId: string) => {
    const { signal } = observationController
    return pollTask<T>(() => fetchSingle<T>(taskId, signal), {
      signal,
      storedTask: () => list.value.find(task => task.id === taskId),
    })
  }

  const updateEntry: TaskActionEntryUpdate = async task => {
    const { id, ...body } = task
    try {
      const response = await api.patch<TaskApiResponseEntry<any>>(
        `tasks/${id}`,
        body,
      )
      const data = response.data
      updateStoreListItem(list.value, data)
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const deleteEntry = async (id: string) => {
    try {
      removeStoreListItem(list.value, id)
      await api.delete(`tasks/${id}`)
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  // CLEAR STORAGE

  const clear = () => {
    list.value = []
    stop()
    observationController.abort(
      new Error('Stopped waiting for the task to finish. Check the task before submitting again.'),
    )
    observationController = new AbortController()
  }

  watch(
    () => userStore.isAuthenticated,
    value => {
      if (!value) return
      ws?.subscribe('tasks', 'get', async (message: WSResponse<TaskList>) => {
        const data = message.data
        if (!data) return
        await setList(data)
      })
    },
    { immediate: true },
  )

  return {
    getList,
    getProcessing,
    getAccountsMapped,
    getFinished,

    commit,

    fetch,
    observe,
    updateEntry,
    deleteEntry,

    clear,
  }
})
